<div class="translation-manager">
  <app-breadcrumb [levels]="[{ label: 'Translation Manager' }]"></app-breadcrumb>
  <div class="translation-manager__layout">
    <aside class="translation-manager__sidebar" aria-label="Translation status filters">
      <div
        class="translation-manager__filter-wrapper"
        [class.is-open]="activeFilterList().length > 0"
      >
        <div class="translation-manager__filter-section">
          <span class="translation-manager__filter-title">Filtering by</span>
          <ul class="translation-manager__filter-list" role="list">
            @for (filter of activeFilterList(); track filter) {
              <li class="translation-manager__filter-tag">
                <button
                  type="button"
                  class="translation-manager__filter-remove"
                  (click)="removeFilter(filter)"
                  [attr.aria-label]="'Remove ' + filter + ' filter'"
                >
                  <mat-icon class="translation-manager__filter-remove-icon" aria-hidden="true">close</mat-icon>
                </button>
                <span class="translation-manager__filter-tag-label">{{ filter }}</span>
              </li>
            }
          </ul>
        </div>
      </div>

      <div class="translation-manager__status-header">
        <span class="translation-manager__status-title">STATUS</span>
      </div>
      <ul class="translation-manager__status-list" role="list">
        @for (status of statusItems(); track status.label) {
          <li
            class="translation-manager__status-item"
            [class.is-active]="isFilterActive(status.label)"
          >
            <button
              type="button"
              class="status-filter-btn"
              (click)="toggleFilter(status.label)"
              [attr.aria-pressed]="isFilterActive(status.label)"
              [class.active]="isFilterActive(status.label)">
              <span class="translation-manager__status-label">
                {{ status.label }}
                <span class="translation-manager__status-tail">
                  ({{ status.count }})
                  @if (hasStatusNotification(status.label)) {
                    <span class="translation-manager__status-dot" aria-label="New notifications"></span>
                  }
                </span>
              </span>
            </button>
          </li>
        }
      </ul>
    </aside>

    <div class="translation-manager__content">
      <section class="translation-manager__table-section" aria-label="Translation jobs">
        @if (errorMessage()) {
          <div class="translation-manager__state translation-manager__state--error" role="status">
            <p class="translation-manager__state-text">{{ errorMessage() }}</p>
          </div>
        } @else if (isLoading()) {
          <div class="translation-manager__state" role="status">
            <p class="translation-manager__state-text">Loading translation jobs...</p>
          </div>
        } @else if (rows().length === 0) {
          <div class="translation-manager__state" role="status">
            <p class="translation-manager__state-text">No translation jobs available.</p>
          </div>
        } @else {
          <div class="translation-manager__table-shell">
            <div class="translation-manager__table-wrapper" role="region" aria-live="polite">
              <table class="translation-manager__table">
                <thead>
                  <tr>
                    <th scope="col">Job ID</th>
                    <th scope="col">Job Name</th>
                    <th
                      scope="col"
                      [attr.aria-sort]="sortColumn() === 'requestDate' ? (sortDescending() ? 'descending' : 'ascending') : 'none'"
                    >
                      <button
                        type="button"
                        class="translation-manager__header-button"
                        (click)="toggleRequestDateSort()"
                        aria-label="Sort by request date"
                      >
                        <span>Request Date</span>
                        <mat-icon class="translation-manager__header-icon" aria-hidden="true">swap_vert</mat-icon>
                      </button>
                    </th>
                    <th scope="col">Source Language</th>
                    <th scope="col">Target Language</th>
                    <th
                      scope="col"
                      [attr.aria-sort]="sortColumn() === 'targetDate' ? (sortAscending() ? 'ascending' : 'descending') : 'none'"
                    >
                      <button
                        type="button"
                        class="translation-manager__header-button"
                        (click)="toggleTargetDateSort()"
                        aria-label="Sort by target date"
                      >
                        <span>Target Date</span>
                        <mat-icon class="translation-manager__header-icon" aria-hidden="true">swap_vert</mat-icon>
                      </button>
                    </th>
                    <th
                      scope="col"
                      [attr.aria-sort]="sortColumn() === 'vendor' ? (sortAscending() ? 'ascending' : 'descending') : 'none'"
                    >
                      <button
                        type="button"
                        class="translation-manager__header-button"
                        (click)="toggleVendorSort()"
                        aria-label="Sort by vendor"
                      >
                        <span>Vendor</span>
                        <mat-icon class="translation-manager__header-icon" aria-hidden="true">swap_vert</mat-icon>
                      </button>
                    </th>
                    <th
                      scope="col"
                      [attr.aria-sort]="sortColumn() === 'status' ? (sortAscending() ? 'ascending' : 'descending') : 'none'"
                    >
                      <button
                        type="button"
                        class="translation-manager__header-button"
                        (click)="toggleStatusSort()"
                        aria-label="Sort by status"
                      >
                        <span>Status</span>
                        <mat-icon class="translation-manager__header-icon" aria-hidden="true">swap_vert</mat-icon>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  @for (row of rows(); track row.jobId) {
                    <tr>
                      <td class="is-link">
                        <span class="translation-manager__job-id">
                          <a
                            [routerLink]="['/translation-manager', row.jobId]"
                            [state]="{ selectedFilters: activeFilterList(), vendorName: row.vendor, jobStatus: row.rawJobStatus, files: row.files, newMessage: row.newMessage }"
                            class="job-id-link"
                          >
                            {{ row.jobId }}
                          </a>
                          @if (hasRowNotification(row)) {
                            <span class="translation-manager__job-id-dot" aria-label="New notifications"></span>
                          }
                        </span>
                      </td>
                      <td class="job-name">{{ row.jobName }}</td>
                      <td>{{ row.requestDate }}</td>
                      <td>
                        <span class="language-chip language-chip--source">{{ row.sourceLanguage }}</span>
                      </td>
                      <td>
                        <div class="language-chip-group">
                          @for (language of row.targetLanguages; track language) {
                            <span class="language-chip language-chip--target">{{ language }}</span>
                          }
                          @if (row.targetOverflowCount > 0) {
                            <button
                              type="button"
                              class="language-chip-overflow-btn"
                              (click)="openLanguagesModal(row)"
                              [attr.aria-label]="'View all ' + (row.targetOverflowCount + 2) + ' target languages'"
                              data-testid="target-overflow-btn"
                            >+{{ row.targetOverflowCount }}</button>
                          }
                        </div>
                      </td>
                      <td>{{ row.targetDate }}</td>
                      <td>{{ row.vendor }}</td>
                      <td>
                        <span [class]="'status-pill status-pill--' + row.statusKey">
                          @if (row.statusIcon) {
                            <mat-icon class="status-pill__icon" aria-hidden="true">{{ row.statusIcon }}</mat-icon>
                          }
                          <span class="status-pill__label">{{ row.statusLabel }}</span>
                        </span>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        }
      </section>
    </div>
  </div>
</div>
